import { useEffect, useRef, useState } from 'react';
import analytics from './analytics';

const ICON_BASE = 'https://community.cloudflare.steamstatic.com/economy/image/';
const TOP_N = 4;
// Cold misses go through the homelab (5s) and may fall back to Steam.
const FETCH_TIMEOUT_MS = 25000;
const MY_STEAMID_KEY = 'pp_my_steamid';
const SEARCH_DEBOUNCE_MS = 1000;

// ---- Affiliates -------------------------------------------------------------
// The only place affiliate links are built. Rendered with target="_blank"
// rel="sponsored noopener".
const CSFLOAT_REF = 'VODKA';
// TODO: set once the Skinport affiliate code arrives, then append it in
// skinportUrl() in whatever format Skinport specifies. While empty, the
// [skinport →] button is hidden: no unpaid links to a marketplace.
const SKINPORT_REF = '';

const csfloatUrl = (name) =>
  `https://csfloat.com/search?market_hash_name=${encodeURIComponent(name)}&ref=${CSFLOAT_REF}`;
const skinportUrl = (itemPage) => itemPage;

// Steam "Type" tags (item.type) grouped for the filter chips. Anything not
// listed lands in "other" (graffiti, medals, music kits, passes, tools).
const CATEGORIES = [
  ['knives & gloves', ['CSGO_Type_Knife', 'Type_Hands']],
  ['weapon skins', ['CSGO_Type_Pistol', 'CSGO_Type_Rifle', 'CSGO_Type_SMG', 'CSGO_Type_SniperRifle',
    'CSGO_Type_Shotgun', 'CSGO_Type_Machinegun', 'CSGO_Type_Equipment', 'CSGO_Type_C4']],
  ['cases', ['CSGO_Type_WeaponCase']],
  ['stickers', ['CSGO_Tool_Sticker']],
  ['charms', ['CSGO_Tool_Keychain']],
  ['agents', ['Type_CustomPlayer']],
  ['patches', ['CSGO_Tool_Patch']],
];
const categoryOf = (type) => (CATEGORIES.find(([, types]) => types.includes(type)) || ['other'])[0];

// Analytics get buckets, never raw values. Always bucketed in USD so a bucket
// means the same thing for every visitor's currency.
export function valueBucket(usd) {
  if (usd < 10) return '<10';
  if (usd < 100) return '10-100';
  if (usd < 1000) return '100-1k';
  if (usd < 10000) return '1k-10k';
  return '10k+';
}

export function formatMoney(amount, currency, digits = 0) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
}

// The API returns every currency's price as a column (see homelab/server.js);
// pick the visitor's, falling back to USD if that map wasn't loaded.
function withDisplayCurrency(data) {
  const currencies = data.currencies || [];
  const code = currencies.includes(data.currency) ? data.currency : 'USD';
  return { ...data, display: code, col: currencies.indexOf(code), usdCol: currencies.indexOf('USD') };
}

const unitPrice = (item, col) => (item.price && col >= 0 ? item.price[col] : null);

function minutesAgo(iso) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / 1440)}d ago`;
}

function ownProfile(steamId) {
  try {
    const mine = localStorage.getItem(MY_STEAMID_KEY);
    return mine ? mine === steamId : 'unknown';
  } catch {
    return 'unknown';
  }
}

// status: 'loading' | 'public' | 'unpriced' | 'private' | 'unavailable'.
// Independent of the Steam and Leetify fetches in App.js; never blocks them.
export function useInventory(steamId) {
  const [inventory, setInventory] = useState({ status: 'loading', data: null });

  useEffect(() => {
    if (!/^\d{17}$/.test(steamId || '')) {
      setInventory({ status: 'unavailable', data: null });
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    setInventory({ status: 'loading', data: null });

    const fail = (reason) => {
      setInventory({ status: 'unavailable', data: null });
      analytics.track('inventory_error', { reason });
    };

    fetch(`/api/inventory/${steamId}`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : { status: 'unavailable', reason: 'other' }))
      .then((data) => {
        if (cancelled) return;
        if (data.status === 'public' || data.status === 'unpriced') {
          setInventory({ status: data.status, data: withDisplayCurrency(data) });
          analytics.track('inventory_loaded', {
            value_bucket: data.status === 'public' && data.usd_total != null
              ? valueBucket(data.usd_total)
              : 'unpriced',
            item_count: data.unit_count,
            cached: data.cached,
            currency: data.currency,
          });
        } else if (data.status === 'private') {
          setInventory({ status: 'private', data });
          analytics.track('inventory_private');
        } else {
          fail(data.reason === '429' || data.reason === 'proxy_failed' ? data.reason : 'other');
        }
      })
      .catch(() => {
        if (!cancelled) fail('other');
      })
      .finally(() => clearTimeout(timer));

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [steamId]);

  return inventory;
}

// "★ StatTrak™ Butterfly Knife | Autotronic" → weapon line + skin line. Items
// without a " | " (cases, music kits, agents' full names) are all skin line.
function splitName(name) {
  const i = name.indexOf(' | ');
  if (i < 0) return { star: false, weapon: '', skin: name };
  const weapon = name.slice(0, i);
  const star = weapon.startsWith('★');
  return { star, weapon: star ? weapon.replace(/^★\s*/, '') : weapon, skin: name.slice(i + 3) };
}

function InventoryItem({ entry, data, list, onAffiliateClick }) {
  const { item, rank } = entry;
  const unit = unitPrice(item, data.col);
  const { star, weapon, skin } = splitName(item.name);

  return (
    <div className="pp-item">
      {item.marketable ? (
        // Same affiliate link as [csfloat →]; hidden from AT/tab order since
        // that text link is the accessible one. No noreferrer (see below).
        // eslint-disable-next-line react/jsx-no-target-blank
        <a className="pp-item-image" href={csfloatUrl(item.market_hash_name)} target="_blank"
          rel="sponsored noopener" tabIndex={-1} aria-hidden="true"
          onClick={() => onAffiliateClick(item, 'csfloat', rank, list, 'image')}>
          <img src={`${ICON_BASE}${item.icon_url}/256fx256f`} alt="" width="160" height="110" loading="lazy" />
        </a>
      ) : (
        <div className="pp-item-image">
          <img src={`${ICON_BASE}${item.icon_url}/256fx256f`} alt="" width="160" height="110" loading="lazy" />
        </div>
      )}
      <div className="pp-item-body">
        <div className="pp-item-weapon">
          {star && <span className="pp-item-star">★ </span>}
          {weapon}
          {item.count > 1 && <span> x{item.count}</span>}
        </div>
        <div className="pp-item-skin">{skin}</div>
        <div className="pp-item-foot">
          {unit == null ? (
            <span className="pp-muted">unpriced</span>
          ) : (
            <span
              className="pp-item-price"
              title={item.count > 1 ? `${formatMoney(unit, data.display, 2)} each` : undefined}
            >
              {formatMoney(unit * item.count, data.display, 2)}
            </span>
          )}
          <span className="pp-item-links">
            {/* No noreferrer: marketplaces may attribute affiliate clicks by Referer. */}
            {SKINPORT_REF && item.skinport_url && (
              // eslint-disable-next-line react/jsx-no-target-blank
              <a className="pp-link" href={skinportUrl(item.skinport_url)} target="_blank" rel="sponsored noopener"
                onClick={() => onAffiliateClick(item, 'skinport', rank, list)}>[skinport →]</a>
            )}
            {item.marketable && (
              // eslint-disable-next-line react/jsx-no-target-blank
              <a className="pp-link" href={csfloatUrl(item.market_hash_name)} target="_blank" rel="sponsored noopener"
                onClick={() => onAffiliateClick(item, 'csfloat', rank, list)}>[csfloat →]</a>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

// Rendered for 'public' and 'unpriced'; private/unavailable hide the card.
// Mount with key={steamId} so "once per page view" state resets per profile.
function InventoryCard({ data, steamId }) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const searchTracked = useRef(false);
  const searchTimer = useRef(null);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const ranked = data.items.map((item, i) => ({ item, rank: i + 1, category: categoryOf(item.type) }));
  const q = query.trim().toLowerCase();
  const visible = expanded
    ? ranked.filter((e) =>
      (category === 'all' || e.category === category) && (!q || e.item.name.toLowerCase().includes(q)))
    : ranked.slice(0, TOP_N);
  // Inventories cached before item types were stored have no type: no chips.
  const hasTypes = data.items.some((item) => item.type);
  // Items (stack units), same unit as the header's "817 items" — not types.
  const counts = {};
  for (const e of ranked) counts[e.category] = (counts[e.category] || 0) + e.item.count;
  const chips = [...CATEGORIES.map(([name]) => name), 'other'].filter((name) => counts[name]);
  const priced = data.status === 'public';

  const onSearch = (e) => {
    setQuery(e.target.value);
    if (searchTracked.current) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      searchTracked.current = true;
      analytics.track('inventory_search');
    }, SEARCH_DEBOUNCE_MS);
  };

  const pickCategory = (name) => {
    setCategory(name);
    if (name !== 'all') analytics.track('inventory_category', { category: name });
  };

  const toggle = () => {
    if (!expanded) analytics.track('inventory_expand');
    setExpanded(!expanded);
  };

  const onAffiliateClick = (item, marketplace, position, list, via = 'link') => {
    const usd = unitPrice(item, data.usdCol);
    analytics.track('affiliate_click', {
      marketplace,
      item: item.market_hash_name,
      price_bucket: usd == null ? 'unpriced' : valueBucket(usd),
      position,
      list,
      own_profile: ownProfile(steamId),
      via,
    });
  };

  if (data.items.length === 0) {
    return (
      <section className="pp-card pp-inventory">
        <div className="pp-inventory-head">
          <span className="pp-comment pp-inventory-title">{'// inventory'}</span>
          <span className="pp-muted">no CS2 items</span>
        </div>
      </section>
    );
  }

  return (
    <section className="pp-card pp-inventory">
      <div className="pp-inventory-head">
        <span className="pp-comment pp-inventory-title">{'// inventory'}</span>
        <span className="pp-muted">
          {expanded
            ? `all ${data.items.length} by price`
            : `top ${Math.min(TOP_N, data.items.length)} of ${data.unit_count.toLocaleString('en-US')} by price`}
        </span>
        <span className="pp-inventory-right">
          {priced ? (
            <span className="pp-inventory-total">{formatMoney(data.totals[data.display], data.display)}</span>
          ) : (
            <span className="pp-muted">{'// prices unavailable'}</span>
          )}
          {data.items.length > TOP_N && (
            <button type="button" className="pp-link pp-link-button" onClick={toggle}>
              {expanded ? `[show top ${TOP_N} ↑]` : '[show all →]'}
            </button>
          )}
        </span>
      </div>
      {expanded && hasTypes && (
        <div className="pp-chips">
          {['all', ...chips].map((name) => (
            <button
              type="button"
              key={name}
              className={`pp-link pp-link-button${category === name ? ' pp-chip-active' : ''}`}
              aria-pressed={category === name}
              onClick={() => pickCategory(name)}
            >
              [{name} {name === 'all' ? data.unit_count : counts[name]}]
            </button>
          ))}
        </div>
      )}
      {expanded && (
        <input
          type="text"
          className="pp-lookup-input pp-inventory-search"
          value={query}
          onChange={onSearch}
          placeholder="filter by name"
          aria-label="Filter inventory by name"
        />
      )}
      <div className="pp-items">
        {visible.map((entry) => (
          <InventoryItem
            key={entry.item.market_hash_name}
            entry={entry}
            data={data}
            list={expanded ? 'full' : 'top4'}
            onAffiliateClick={onAffiliateClick}
          />
        ))}
      </div>
      {expanded && visible.length === 0 && (
        <div className="pp-muted">{'// no items match'}</div>
      )}
      <div className="pp-inventory-footer">
        {priced
          ? `// prices: skinport suggested · updated ${minutesAgo(data.prices_fetched_at)}`
          : '// prices: temporarily unavailable · items listed unpriced'}
      </div>
    </section>
  );
}

// Service medals, coins and badges: non-marketable collectibles. Pins share the
// Collectible type but are marketable, so they stay out. Inventories cached
// before item types were stored have no type and show no medals.
export function Medals({ items }) {
  const medals = items.filter((item) => item.type === 'CSGO_Type_Collectible' && !item.marketable);
  if (!medals.length) return null;
  return (
    <div className="pp-medals">
      <div className="pp-comment">{`// medals (${medals.length})`}</div>
      <div className="pp-medals-grid">
        {medals.map((m) => (
          <img key={m.market_hash_name} src={`${ICON_BASE}${m.icon_url}/64fx64f`}
            alt={m.name} title={m.name} width="40" height="30" loading="lazy" />
        ))}
      </div>
    </div>
  );
}

export default InventoryCard;
